import { mediaSrc } from "../../lib/api";
import { initials } from "../../lib/format";
import { RichText } from "./Blocks";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../ui/sheet";
import { htmlToText, looksStructured, structureText } from "../../lib/structureText";

const PROFILE_CLS = "[&_h3]:mb-3 [&_h3]:mt-8 [&_h3]:border-b [&_h3]:border-gold/50 [&_h3]:pb-2 [&_h3]:font-serif [&_h3]:text-xl [&_h3]:text-navy [&_li]:mb-2 [&_li::marker]:text-burgundy";

// Pasted plain text rendered as headings/bullets/paragraphs (React text nodes only, so nothing is injected as HTML)
function Structured({ text, testid }) {
  return (
    <div className={`mt-2 space-y-3 leading-relaxed ${PROFILE_CLS}`} data-testid={testid}>
      {structureText(text).map((b, i) => (b.type === "h3" ? <h3 key={i}>{b.text}</h3>
        : b.type === "ul" ? <ul key={i} className="list-disc pl-6">{b.items.map((t, j) => <li key={j}>{t}</li>)}</ul>
          : <p key={i}>{b.text}</p>))}
    </div>
  );
}

export const speakerSlug = (s) => s.slug || (s.name || "").toLowerCase().replace(/\(.*?\)/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export function SpeakerAvatar({ s, size = "h-36 w-36", text = "text-4xl" }) {
  if (s.photo_url) return <img src={mediaSrc(s.photo_url)} alt={s.name} className={`${size} rounded-full object-cover ring-4 ring-white shadow-md`} />;
  return <span className={`${size} ${text} flex items-center justify-center rounded-full bg-navy font-serif text-gold ring-4 ring-gold/40`} aria-hidden="true">{initials(s.name)}</span>;
}

export function SpeakerCard({ s, onOpen }) {
  const slug = speakerSlug(s);
  return (
    <li id={slug} className="group flex flex-col items-center rounded-xl border-t-4 border-gold bg-white px-6 pb-7 pt-8 text-center shadow-sm ring-1 ring-slate-200 transition-shadow duration-300 hover:shadow-lg" data-testid={`speaker-card-${slug}`}>
      <SpeakerAvatar s={s} />
      <h2 className="mt-5 font-serif text-xl text-navy">{s.name}</h2>
      {s.post_nominals && <p className="mt-1 text-xs font-semibold tracking-[0.08em] text-[#8A6D1F]">{s.post_nominals}</p>}
      {s.title && <p className="mt-2 text-sm font-semibold text-burgundy">{s.title}</p>}
      {s.organization && <p className="text-sm text-[#4A5568]">{s.organization}</p>}
      <button type="button" onClick={() => onOpen(s)} className="mt-auto inline-flex items-center rounded-full border-2 border-navy px-5 py-2 pt-2 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-navy hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-burgundy [margin-top:1.25rem]"
        aria-label={`Read bio: ${s.name}`} data-testid={`speaker-read-bio-${slug}`}>Read bio</button>
    </li>
  );
}

export function SpeakerProfile({ s, onClose }) {
  return (
    <Sheet open={!!s} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto border-l-4 border-gold p-0 sm:max-w-2xl [&>button]:text-white [&>button]:opacity-100" data-testid="speaker-profile">
        {s && <>
          <header className="bg-navy px-8 pb-8 pt-12 text-white">
            <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
              <SpeakerAvatar s={s} size="h-40 w-40" text="text-5xl" />
              <div>
                <SheetTitle className="font-serif text-3xl text-white" data-testid="speaker-profile-name">{s.name}</SheetTitle>
                {s.post_nominals && <p className="mt-2 text-sm font-semibold tracking-[0.1em] text-gold">{s.post_nominals}</p>}
                {s.title && <p className="mt-3 text-base font-semibold text-white">{s.title}</p>}
                <SheetDescription className="mt-1 text-sm text-slate-300">{s.subtitle || s.organization}</SheetDescription>
              </div>
            </div>
          </header>
          <div className="px-8 py-8 text-base text-[#2D3748]">
            {s.bio && (looksStructured(s.bio) ? <Structured text={s.bio} testid="speaker-profile-intro" />
              : <p className="text-lg leading-relaxed text-navy" data-testid="speaker-profile-intro">{s.bio}</p>)}
            {s.profile && (/[•●▪]/.test(s.profile) || (!/<(h\d|ul|ol)\b/i.test(s.profile) && looksStructured(htmlToText(s.profile)))
              ? <Structured text={htmlToText(s.profile)} testid="speaker-profile-body" />
              : <RichText html={s.profile} testid="speaker-profile-body" className={`mt-6 text-base ${PROFILE_CLS}`} />)}
          </div>
        </>}
      </SheetContent>
    </Sheet>
  );
}
